import { clsx } from 'clsx';
import { Eye, EyeOff, FileUp, Pencil, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { BottomSheet } from '@/components/ui/BottomSheet';
import type { ManagedFileReport } from '@/types/reports.types';

interface FileReportActionsSheetProps {
  report: ManagedFileReport | null;
  onClose: () => void;
  onEdit: (report: ManagedFileReport) => void;
  onReplace: (report: ManagedFileReport) => void;
  onToggleVisibility: (report: ManagedFileReport) => void;
  onDelete: (report: ManagedFileReport) => void;
}

/** The per-file admin menu: a bottom sheet on phones, a small centered panel on desktop. */
export function FileReportActionsSheet({
  report,
  onClose,
  onEdit,
  onReplace,
  onToggleVisibility,
  onDelete,
}: FileReportActionsSheetProps) {
  const run = (action: (report: ManagedFileReport) => void) => () => {
    if (!report) return;
    onClose();
    action(report);
  };

  return (
    <BottomSheet
      isOpen={!!report}
      onClose={onClose}
      title={report?.fileCaption ?? ''}
      subtitle={report ? `${report.fileCategory} · ${report.fileName}` : undefined}
    >
      <div className="flex flex-col gap-1 pb-2">
        <ActionButton icon={Pencil} label="Edit details" onClick={run(onEdit)} />
        <ActionButton icon={FileUp} label="Replace file" onClick={run(onReplace)} />
        <ActionButton
          icon={report?.visible ? EyeOff : Eye}
          label={report?.visible ? 'Hide from customers' : 'Show to customers'}
          onClick={run(onToggleVisibility)}
        />
        <ActionButton icon={Trash2} label="Delete" onClick={run(onDelete)} danger />
      </div>
    </BottomSheet>
  );
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  danger = false,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
          : 'text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10'
      )}
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {label}
    </button>
  );
}
